/**
 * Парсер для PlantUML диаграмм последовательности
 */

export const parsePlantUml = (text, ElementFactory, options = {}) => {
  const { systemsMap } = options; // Map<code, system> из реестра ИС
  const lines = (text || '').split(/\r?\n/).map(l => l.trim());
  const participants = [];
  const defs = {};
  const events = [];
  const msgs = [];
  const implicitActors = new Set(); // неявные участники для пометки ошибкой

  // Регулярные выражения для парсинга
  const partRe = /^participant\s+(\S+)(?:\s+as\s+"([^"]*)")?$/i;
  const dbRe = /^database\s+(\S+)(?:\s+as\s+"([^"]*)")?$/i;
  const actorRe = /^actor\s+(\S+)(?:\s+as\s+"([^"]*)")?$/i;
  const msgRight = /(\S+)\s*->\s*(\S+)\s*:(.+)$/;
  const msgLeft = /(\S+)\s*<-\s*(\S+)\s*:(.+)$/;
  const msgDashRight = /(\S+)\s*-->\s*(\S+)\s*:(.+)$/;
  const msgDashLeft = /(\S+)\s*<--\s*(\S+)\s*:(.+)$/;
  const actRe = /^activate\s+(\S+)/i;
  const deactRe = /^deactivate\s+(\S+)/i;
  const noteRe = /^note\s+(left|right|over)\s+(?:of\s+)?(\S+)(?:\s*:\s*(.*))?$/i;
  // Новые конструкции: alt/opt/else/end
  const altRe = /^alt\s+(.+)$/i;
  const optRe = /^opt\s+(.+)$/i;
  const elseRe = /^else(?:\s+(.+))?$/i;
  const endRe = /^(?:end|endif)$/i;

  let inBlock = false;
  let inNote = false;
  let currentNote = null;

  // Стек рамок alt/opt
  const frameStack = [];
  // Индекс строки сообщений/заметок (для вертикального позиционирования)
  let msgRow = 0;

  // Парсинг строк
  lines.forEach(line => {
    if (/^@startuml/i.test(line)) {
      inBlock = true;
      return;
    }
    if (/^@enduml/i.test(line)) {
      inBlock = false;
      return;
    }
    if (!inBlock) return;

    let m;

    // Рамки: ALT/OPT/ELSE/END
    if (!inNote && (m = altRe.exec(line))) {
      const cond = (m[1] || '').trim();
      frameStack.push({ kind: 'alt', header: cond, startRow: msgRow, sections: [{ title: cond, startRow: msgRow }] });
      return;
    }
    if (!inNote && (m = optRe.exec(line))) {
      const cond = (m[1] || '').trim();
      frameStack.push({ kind: 'opt', header: cond, startRow: msgRow, sections: [{ title: cond, startRow: msgRow }] });
      return;
    }
    if (!inNote && (m = elseRe.exec(line))) {
      const top = frameStack[frameStack.length - 1];
      if (top) {
        // закрываем предыдущую секцию
        const last = top.sections[top.sections.length - 1];
        if (last && last.endRow == null) last.endRow = msgRow;
        // открываем новую секцию
        const title = (m[1] || 'else').trim();
        top.sections.push({ title, startRow: msgRow });
      }
      return;
    }
    if (!inNote && endRe.test(line)) {
      const top = frameStack.pop();
      if (top) {
        const last = top.sections[top.sections.length - 1];
        if (last && last.endRow == null) last.endRow = msgRow;
        const frameEvent = {
          type: 'frame',
          kind: top.kind,
          startRow: top.startRow,
          endRow: msgRow,
          sections: top.sections
        };
        events.push(frameEvent);
      }
      return;
    }

    // Note start
    if (!inNote && (m = noteRe.exec(line))) {
      const position = m[1].toLowerCase(); // left, right, over
      const actor = m[2];
      const text = m[3] || '';

      if (text) {
        // Однострочная заметка
        events.push({ type: 'note', position, actor, text, row: msgRow });
        msgRow++;
      } else {
        // Многострочная заметка
        inNote = true;
        currentNote = { position, actor, text: [] };
      }
      return;
    }

    // Note content
    if (inNote && line !== 'end note' && !/^end\s+note$/i.test(line)) {
      currentNote.text.push(line);
      return;
    }

    // Note end
    if (inNote && /^end\s+note$/i.test(line)) {
      events.push({
        type: 'note',
        position: currentNote.position,
        actor: currentNote.actor,
        text: currentNote.text.join('\n'),
        row: msgRow
      });
      inNote = false;
      currentNote = null;
      msgRow++;
      return;
    }

    // Participant
    if ((m = partRe.exec(line))) {
      const n = m[1];
      const lbl = m[2] || n;
      if (!defs[n]) {
        // Определяем model и другие поля из реестра ИС
        const entity = systemsMap?.get(n);
        const model = entity?.model || 'service';
        defs[n] = {
          kind: 'participant',
          label: lbl,
          code: n,
          model,
          entityType: entity?.type || 'module', // 'system' или 'module'
          systemId: entity?.systemId || null,
          systemCode: entity?.systemCode || null,
          systemName: entity?.systemName || null
        };
        participants.push(n);
      }
      return;
    }

    // Database
    if ((m = dbRe.exec(line))) {
      const n = m[1];
      const lbl = m[2] || n;
      if (!defs[n]) {
        // Определяем model и другие поля из реестра ИС
        const entity = systemsMap?.get(n);
        const model = entity?.model || 'db';
        defs[n] = {
          kind: 'database',
          label: lbl,
          code: n,
          model,
          entityType: entity?.type || 'module',
          systemId: entity?.systemId || null,
          systemCode: entity?.systemCode || null,
          systemName: entity?.systemName || null
        };
        participants.push(n);
      }
      return;
    }

    // Actor (external user/system — not validated against registry)
    if ((m = actorRe.exec(line))) {
      const n = m[1];
      const lbl = m[2] || n;
      if (!defs[n]) {
        defs[n] = {
          kind: 'actor',
          label: lbl,
          code: n,
          model: 'actor',
          entityType: 'actor',
          systemId: null,
          systemCode: null,
          systemName: null
        };
        participants.push(n);
      }
      return;
    }

    // Activate
    if ((m = actRe.exec(line))) {
      const w = m[1];
      if (!defs[w]) {
        const system = systemsMap?.get(w);
        const model = system?.model || 'service';
        defs[w] = { kind: 'participant', label: w, code: w, model };
        participants.push(w);
      }
      events.push({ type: 'activate', who: w });
      return;
    }

    // Deactivate
    if ((m = deactRe.exec(line))) {
      const w = m[1];
      if (!defs[w]) {
        const system = systemsMap?.get(w);
        const model = system?.model || 'service';
        defs[w] = { kind: 'participant', label: w, code: w, model };
        participants.push(w);
      }
      events.push({ type: 'deactivate', who: w });
      return;
    }

    // Messages
    if ((m = msgDashRight.exec(line))) {
      const f = m[1], t = m[2], txt = m[3].trim();
      // Автосоздание неявных участников
      [f, t].forEach(actor => {
        if (!defs[actor]) {
          const entity = systemsMap?.get(actor);
          const isInRegistry = !!entity;
          defs[actor] = {
            kind: 'participant',
            label: actor,
            code: actor,
            model: entity?.model || 'service',
            entityType: entity?.type || 'module',
            systemId: entity?.systemId || null,
            systemCode: entity?.systemCode || null,
            systemName: entity?.systemName || null,
            isImplicit: true,
            hasRegistryError: !isInRegistry
          };
          participants.push(actor);
          if (!isInRegistry) implicitActors.add(actor);
        }
      });
      msgs.push({ from: f, to: t, text: txt, dashed: true });
      events.push({ type: 'msg', from: f, to: t, text: txt, dashed: true, row: msgRow });
      msgRow++;
      return;
    }

    if ((m = msgDashLeft.exec(line))) {
      const l = m[1], r = m[2], txt = m[3].trim();
      [l, r].forEach(actor => {
        if (!defs[actor]) {
          const entity = systemsMap?.get(actor);
          const isInRegistry = !!entity;
          defs[actor] = {
            kind: 'participant',
            label: actor,
            code: actor,
            model: entity?.model || 'service',
            entityType: entity?.type || 'module',
            systemId: entity?.systemId || null,
            systemCode: entity?.systemCode || null,
            systemName: entity?.systemName || null,
            isImplicit: true,
            hasRegistryError: !isInRegistry
          };
          participants.push(actor);
          if (!isInRegistry) implicitActors.add(actor);
        }
      });
      msgs.push({ from: r, to: l, text: txt, dashed: true });
      events.push({ type: 'msg', from: r, to: l, text: txt, dashed: true, row: msgRow });
      msgRow++;
      return;
    }

    if ((m = msgRight.exec(line))) {
      const f = m[1], t = m[2], txt = m[3].trim();
      [f, t].forEach(actor => {
        if (!defs[actor]) {
          const entity = systemsMap?.get(actor);
          const isInRegistry = !!entity;
          defs[actor] = {
            kind: 'participant',
            label: actor,
            code: actor,
            model: entity?.model || 'service',
            entityType: entity?.type || 'module',
            systemId: entity?.systemId || null,
            systemCode: entity?.systemCode || null,
            systemName: entity?.systemName || null,
            isImplicit: true,
            hasRegistryError: !isInRegistry
          };
          participants.push(actor);
          if (!isInRegistry) implicitActors.add(actor);
        }
      });
      msgs.push({ from: f, to: t, text: txt, dashed: false });
      events.push({ type: 'msg', from: f, to: t, text: txt, dashed: false, row: msgRow });
      msgRow++;
      return;
    }

    if ((m = msgLeft.exec(line))) {
      const l = m[1], r = m[2], txt = m[3].trim();
      [l, r].forEach(actor => {
        if (!defs[actor]) {
          const entity = systemsMap?.get(actor);
          const isInRegistry = !!entity;
          defs[actor] = {
            kind: 'participant',
            label: actor,
            code: actor,
            model: entity?.model || 'service',
            entityType: entity?.type || 'module',
            systemId: entity?.systemId || null,
            systemCode: entity?.systemCode || null,
            systemName: entity?.systemName || null,
            isImplicit: true,
            hasRegistryError: !isInRegistry
          };
          participants.push(actor);
          if (!isInRegistry) implicitActors.add(actor);
        }
      });
      msgs.push({ from: r, to: l, text: txt, dashed: false });
      events.push({ type: 'msg', from: r, to: l, text: txt, dashed: false, row: msgRow });
      msgRow++;
      return;
    }
  });

  // Генерация элементов с использованием переданного ElementFactory
  return generateSequenceDiagram(participants, defs, events, ElementFactory, implicitActors);
};

const estimateTextWidth = (text, fontSize = 18, fontFamily = 'Arial') => {
  // Простая эвристика: ширина символа * длина
  // Для более точного результата можно использовать canvas.measureText, но здесь достаточно
  const avgCharWidth = fontSize * 0.6; // эмпирически
  return Math.max(60, text.length * avgCharWidth + 32); // +32 — запас на паддинги
};

const generateSequenceDiagram = (participants, defs, events, ElementFactory, implicitActors) => {
  const baseX = 120;
  const yTop = 60;
  const msgSpacing = 60;
  const headerH = 40;
  const minLL = 220;
  const minGap = 80; // минимальный отступ между участниками
  const actorShapeW = 60;
  const actorShapeH = 100;

  // If any participant is an actor, use actorShapeH as the effective header height
  // so messages start below all participant shapes
  const hasActors = participants.some(n => defs[n].kind === 'actor');
  const effectiveHeaderH = hasActors ? actorShapeH : headerH;

  // 1. Считаем ширину для каждого participant
  const widths = participants.map(n => {
    const d = defs[n];
    // Actors use fixed icon width but need space for label too
    if (d.kind === 'actor') return Math.max(actorShapeW + 20, estimateTextWidth(d.label || n, 14));
    return estimateTextWidth(d.label || n, 18);
  });

  // 2. Считаем x-координаты с учётом ширины и отступа
  const xOf = {};
  let curX = baseX;
  for (let i = 0; i < participants.length; ++i) {
    xOf[participants[i]] = curX + widths[i] / 2;
    curX += widths[i] + minGap;
  }

  const maxRow = events.reduce((acc, e) => (typeof e.row === 'number' ? Math.max(acc, e.row) : acc), -1);
  const lifelineH = Math.max(minLL, (maxRow + 1) * msgSpacing + 160);

  const out = [];

  // 3. Создание участников
  participants.forEach((n, idx) => {
    const d = defs[n];
    const x = xOf[n];
    const model = d.model || 'service';
    const systemCode = d.code;
    const entityType = d.entityType;
    const parentSystemId = d.systemId;
    const parentSystemCode = d.systemCode;
    const parentSystemName = d.systemName;
    const hasError = implicitActors.has(n) && d.kind !== 'actor';
    const w = widths[idx], h = headerH;

    if (d.kind === 'actor') {
      const top = ElementFactory.actor(x - actorShapeW / 2, yTop);
      top._semanticId = `ptop:${n}`;
      const bottom = ElementFactory.actor(x - actorShapeW / 2, yTop + lifelineH);
      bottom._semanticId = `pbot:${n}`;
      const line = ElementFactory.arrow(x, yTop + actorShapeH, x, yTop + lifelineH);
      line._semanticId = `lifeline:${n}`;
      line.startCap = 'none';
      line.endCap = 'none';
      line.dashed = true;
      line.lifeline = {
        start: { elementId: top.id, side: 'bottom' },
        end: { elementId: bottom.id, side: 'top' }
      };
      const lt = ElementFactory.text(x, yTop + actorShapeH + 14, d.label);
      lt._semanticId = `plabel-top:${n}`;
      const lb = ElementFactory.text(x, yTop + lifelineH + actorShapeH + 14, d.label);
      lb._semanticId = `plabel-bot:${n}`;
      out.push(top, bottom, line, lt, lb);
    } else if (d.kind === 'participant') {
      const top = ElementFactory.rectangle(x - w / 2, yTop, w, h, {
        connectable: false, model, systemCode, entityType,
        parentSystemId, parentSystemCode, parentSystemName,
        ...(hasError && { registryError: true })
      });
      top._semanticId = `ptop:${n}`;
      const bottom = ElementFactory.rectangle(x - w / 2, yTop + lifelineH, w, h, {
        connectable: false, model, systemCode, entityType,
        parentSystemId, parentSystemCode, parentSystemName,
        ...(hasError && { registryError: true })
      });
      bottom._semanticId = `pbot:${n}`;
      const line = ElementFactory.arrow(x, yTop + h, x, yTop + lifelineH);
      line._semanticId = `lifeline:${n}`;
      line.startCap = 'none';
      line.endCap = 'none';
      line.dashed = true;
      line.lifeline = {
        start: { elementId: top.id, side: 'bottom' },
        end: { elementId: bottom.id, side: 'top' }
      };
      const lt = ElementFactory.text(x, yTop + h / 2, d.label);
      lt._semanticId = `plabel-top:${n}`;
      lt.attachedToElement = top.id;
      const lb = ElementFactory.text(x, yTop + lifelineH + h / 2, d.label);
      lb._semanticId = `plabel-bot:${n}`;
      lb.attachedToElement = bottom.id;
      out.push(top, bottom, line, lt, lb);
    } else { // database
      const w = widths[idx], h = headerH;
      const top = ElementFactory.database(x - w / 2, yTop, w, h, {
        connectable: false, model, systemCode, entityType,
        parentSystemId, parentSystemCode, parentSystemName,
        ...(hasError && { registryError: true })
      });
      top._semanticId = `ptop:${n}`;
      const bottom = ElementFactory.database(x - w / 2, yTop + lifelineH, w, h, {
        connectable: false, model, systemCode, entityType,
        parentSystemId, parentSystemCode, parentSystemName,
        ...(hasError && { registryError: true })
      });
      bottom._semanticId = `pbot:${n}`;
      const ry = Math.min(h * 0.15, w * 0.2);
      const line = ElementFactory.arrow(x, yTop + h - ry, x, yTop + lifelineH);
      line._semanticId = `lifeline:${n}`;
      line.startCap = 'none';
      line.endCap = 'none';
      line.dashed = true;
      line.lifeline = {
        start: { elementId: top.id, side: 'bottom' },
        end: { elementId: bottom.id, side: 'top' }
      };
      const lt = ElementFactory.text(x, yTop + h / 2, d.label);
      lt._semanticId = `plabel-top:${n}`;
      lt.attachedToElement = top.id;
      const lb = ElementFactory.text(x, yTop + lifelineH + h / 2, d.label);
      lb._semanticId = `plabel-bot:${n}`;
      lb.attachedToElement = bottom.id;
      out.push(top, bottom, line, lt, lb);
    }
  });

  // Подготовка отступов по строкам для рамок
  const rowOffset = new Map();
  const addOffset = (row, delta) => { if (row == null) return; rowOffset.set(row, (rowOffset.get(row) || 0) + delta); };

  const frames = events.filter(e => e.type === 'frame');
  frames.forEach(fr => {
    // Немного отступа от верхней/нижней границы рамки
    addOffset(fr.startRow, 8);
    addOffset(fr.endRow, 6);
    // Соседние сообщения вне рамки слегка отталкиваем
    addOffset(fr.startRow - 1, -8);
    addOffset(fr.endRow, 8);

    if (fr.kind === 'alt') {
      // Первая секция – небольшой отступ
      if (fr.sections?.[0]) addOffset(fr.sections[0].startRow, 8);
      // Для секций после else – больший отступ, чтобы текст первой стрелки было ниже разделителя
      for (let i = 1; i < (fr.sections?.length || 0); i++) addOffset(fr.sections[i].startRow, 16);
    } else if (fr.kind === 'opt') {
      // Для opt тоже небольшой стартовый отступ
      addOffset(fr.startRow, 10);
    }
  });

  // Создание сообщений/заметок/рамок, привязка к рядам
  const baseMsgY = yTop + effectiveHeaderH + 40;
  const yOfRow = (row) => baseMsgY + (row || 0) * msgSpacing + (rowOffset.get(row || 0) || 0);

  // Content-based message IDs: from:to:text:occurrence (positional IDs break on insertion)
  const msgCounts = {};
  events.forEach(ev => {
    if (ev.type === 'msg') {
      const y = yOfRow(ev.row);
      const x1 = xOf[ev.from];
      const x2 = xOf[ev.to];
      const msgKey = `${ev.from}:${ev.to}:${ev.text}`;
      msgCounts[msgKey] = (msgCounts[msgKey] || 0) + 1;
      const semanticBase = `msg:${msgKey}:${msgCounts[msgKey]}`;

      if (ev.from === ev.to) {
        const dx = 80, dy = 40;
        const pts = [ { x: x1, y }, { x: x1 + dx, y }, { x: x1 + dx, y: y + dy }, { x: x1, y: y + dy } ];
        const ar = ElementFactory.arrow(x1, y, x1, y + dy);
        ar._semanticId = `${semanticBase}:arrow`;
        ar.pathPoints = pts;
        ar.startCap = 'none';
        ar.endCap = 'arrow';
        if (ev.dashed) ar.dashed = true;
        ar.isMessage = true;
        ar.fromActor = ev.from;
        ar.toActor = ev.to;
        ar.messageText = ev.text;
        const tx = (pts[1].x + pts[2].x) / 2;
        const ty = pts[1].y - 12;
        const textEl = ElementFactory.text(tx, ty, ev.text);
        textEl._semanticId = `${semanticBase}:text`;
        out.push(ar, textEl);
      } else {
        const ar = ElementFactory.arrow(x1, y, x2, y);
        ar._semanticId = `${semanticBase}:arrow`;
        ar.startCap = 'none';
        ar.endCap = 'arrow';
        if (ev.dashed) ar.dashed = true;
        ar.isMessage = true;
        ar.fromActor = ev.from;
        ar.toActor = ev.to;
        ar.messageText = ev.text;
        const mid = (x1 + x2) / 2;
        const textEl = ElementFactory.text(mid, y - 12, ev.text);
        textEl._semanticId = `${semanticBase}:text`;
        out.push(ar, textEl);
      }
    } else if (ev.type === 'note') {
      const y = yOfRow(ev.row);
      const actorX = xOf[ev.actor];
      let noteX = actorX;
      const noteWidth = 150;
      const noteOffsetX = 80;
      if (ev.position === 'left') noteX = actorX - noteWidth - noteOffsetX;
      else if (ev.position === 'right') noteX = actorX + noteOffsetX;
      else if (ev.position === 'over') noteX = actorX - noteWidth / 2;
      const lines = ev.text.split('\n');
      const lineHeight = 18;
      const padding = 8;
      const noteHeight = lines.length * lineHeight + padding * 2;
      const noteRect = ElementFactory.rectangle(noteX, y - noteHeight / 2, noteWidth, noteHeight, { fill: '#ffffcc', stroke: '#999999', strokeWidth: 1, borderRadius: 4 });
      noteRect._semanticId = `note:${ev.row}:rect`;
      const noteTexts = lines.map((line, i) => {
        const textY = y - noteHeight / 2 + padding + lineHeight / 2 + i * lineHeight;
        const text = ElementFactory.text(noteX + noteWidth / 2, textY, line);
        text._semanticId = `note:${ev.row}:text:${i}`;
        text.fontSize = 13;
        text.fill = '#333333';
        return text;
      });
      out.push(noteRect, ...noteTexts);
    } else if (ev.type === 'frame') {
      const inside = events.filter(e => (e.type === 'msg' || e.type === 'note') && typeof e.row === 'number' && e.row >= ev.startRow && e.row < ev.endRow);
      let minX = Infinity, maxX = -Infinity;
      inside.forEach(it => {
        if (it.type === 'msg') { minX = Math.min(minX, xOf[it.from], xOf[it.to]); maxX = Math.max(maxX, xOf[it.from], xOf[it.to]); }
        else if (it.type === 'note') { const ax = xOf[it.actor]; if (ax != null) { minX = Math.min(minX, ax); maxX = Math.max(maxX, ax); } }
      });
      const centers = participants.map(n => xOf[n]);
      const fallbackLeft = Math.min(...centers) - 140;
      const fallbackRight = Math.max(...centers) + 140;
      const marginX = 90;
      const leftX = isFinite(minX) ? Math.min(minX, fallbackRight) - marginX : fallbackLeft;
      const rightX = isFinite(maxX) ? Math.max(maxX, fallbackLeft) + marginX : fallbackRight;
      const frameLeftX = Math.min(leftX, rightX - 100);
      const frameRightX = Math.max(rightX, frameLeftX + 100);
      const frameWidth = frameRightX - frameLeftX;
      const padY = 24;
      const yStart = yOfRow(ev.startRow) - padY;
      const yEnd = yOfRow(ev.endRow) + padY;
      const height = Math.max(40, yEnd - yStart);

      const frameRect = ElementFactory.rectangle(frameLeftX, yStart, frameWidth, height, { fill: 'transparent', stroke: '#555555', strokeWidth: 1.5, borderRadius: 6, isFrame: true, frameKind: ev.kind });
      frameRect._semanticId = `frame:${ev.startRow}:rect`;
      const labelW = 160; const labelH = 22;
      const labelRect = ElementFactory.rectangle(frameLeftX + 10, yStart - labelH / 2, labelW, labelH, { fill: '#ffffff', stroke: '#555555', strokeWidth: 1, borderRadius: 4 });
      labelRect._semanticId = `frame:${ev.startRow}:label-rect`;
      const title = `${ev.kind.toUpperCase()}: ${ev.sections?.[0]?.title || ''}`;
      const labelText = ElementFactory.text(frameLeftX + 10 + labelW / 2, yStart - labelH / 2 + labelH / 2, title);
      labelText._semanticId = `frame:${ev.startRow}:label-text`;
      labelText.fontSize = 13; labelText.fill = '#333333';
      out.push(frameRect, labelRect, labelText);

      if (ev.kind === 'alt' && Array.isArray(ev.sections)) {
        for (let i = 1; i < ev.sections.length; i++) {
          const sec = ev.sections[i];
          const sepY = yOfRow(sec.startRow) - 6;
          const sep = ElementFactory.arrow(frameLeftX + 6, sepY, frameLeftX + frameWidth - 6, sepY);
          sep._semanticId = `frame:${ev.startRow}:sep:${i}:arrow`;
          sep.startCap = 'none'; sep.endCap = 'none'; sep.dashed = false;
          sep.isFrameSeparator = true;
          const secLabelW = 160; const secLabelH = 20;
          const secRect = ElementFactory.rectangle(frameLeftX + 10, sepY - secLabelH / 2, secLabelW, secLabelH, { fill: '#ffffff', stroke: '#555555', strokeWidth: 1, borderRadius: 4 });
          secRect._semanticId = `frame:${ev.startRow}:sep:${i}:rect`;
          const secText = ElementFactory.text(frameLeftX + 10 + secLabelW / 2, sepY, `else: ${sec.title || ''}`);
          secText._semanticId = `frame:${ev.startRow}:sep:${i}:text`;
          secText.fontSize = 12; secText.fill = '#333333';
          out.push(sep, secRect, secText);
        }
      }
    }
  });

  return out;
};

export const isPlantUmlCode = (code) => {
  return /@startuml/i.test(code) || /(\bparticipant\b|\bdatabase\b|\bactor\b|--?>|<--?|->|<-|\balt\b|\bopt\b)/.test(code);
};
